// P0 · Acceso (documento 07, decisión D51). Como todos: el correo y la contraseña o, si el servidor los tiene, Google,
// Apple o Facebook, que vuelven a esta misma pantalla con `#c=<código>&p=<proveedor>` o `#error=`. La contraseña la
// crea el chofer con el enlace que le manda el administrador. El celular con un código solo aparece (como un enlace
// discreto abajo) si alguna empresa lo prendió: esa pantalla vive aparte (AccesoCelular).
import { mensajeGooglePersonal, NOMBRE_PROVEEDOR, type Proveedor } from '@reparto/shared/nucleo';
import { Boton, BotonProveedor, Campo, Esqueleto, useToast, ZonaCargando } from '@reparto/ui';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { CampoClave } from '../componentes/CampoClave';
import { PantallaNeutra } from '../componentes/Esqueletos';
import { AvisoError, EnlacePie, type EstadoAcceso, MarcoAcceso, SeparadorO } from '../componentes/MarcoAcceso';
import { ErrorApi, SinRed } from '../datos/api';
import { correoValido } from '../datos/clave';
import {
  causaDelError,
  irAProveedor,
  type OpcionesAcceso,
  opcionesAcceso,
  proveedoresDe,
  proveedoresRecordados,
  tomarSecreto,
  tomarVuelta,
} from '../datos/google';
import { entrarConClave, entrarConProveedor, ultimoCorreo } from '../datos/sesion';

const SIN_RED = 'No hay conexión. Para entrar necesitás señal; después la app funciona sin señal.';

export function Acceso() {
  const navegar = useNavigate();
  const estado = useLocation().state as EstadoAcceso | null;
  const { mostrar } = useToast();
  const [email, setEmail] = useState(() => estado?.email ?? ultimoCorreo());
  const [password, setPassword] = useState('');
  const [errores, setErrores] = useState<{ email?: string; password?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** undefined: preguntando; null: sin señal o sin respuesta (solo el correo y la contraseña). */
  const [opciones, setOpciones] = useState<OpcionesAcceso | null | undefined>(undefined);
  // Mientras llegan las opciones, el lugar de los botones que hubo la última vez en este celular.
  const [recordados] = useState(proveedoresRecordados);
  const [errorProveedor, setErrorProveedor] = useState<string | null>(null);
  /** El proveedor con que se está yendo (ida) o con el que volvió y se canjea el código (vuelta). */
  const [conProveedor, setConProveedor] = useState<{ p: Proveedor; paso: 'ida' | 'vuelta' } | null>(null);
  // Una sola vez aunque React monte dos veces en desarrollo: el código y el secreto se gastan al usarlos.
  const vueltaLeida = useRef(false);
  const proveedores = proveedoresDe(opciones ?? null);
  const esperando = opciones === undefined && recordados > 0;

  useEffect(() => {
    let vivo = true;
    void opcionesAcceso().then((o) => vivo && setOpciones(o));
    // Volver atrás desde el proveedor restaura la pantalla tal como quedó: el botón se libera.
    const alMostrar = (e: PageTransitionEvent) => e.persisted && setConProveedor(null);
    window.addEventListener('pageshow', alMostrar);
    return () => {
      vivo = false;
      window.removeEventListener('pageshow', alMostrar);
    };
  }, []);

  useEffect(() => {
    if (vueltaLeida.current) return;
    vueltaLeida.current = true;
    const v = tomarVuelta();
    if (!v || v.vincular) return;
    const p = v.proveedor;
    const secreto = v.codigo ? tomarSecreto() : null;
    if (v.error || !v.codigo || !secreto) {
      setErrorProveedor(mensajeGooglePersonal(v.error ?? 'vencido', 'chofer', p));
      return;
    }
    setConProveedor({ p, paso: 'vuelta' });
    entrarConProveedor(p, v.codigo, secreto)
      .then((s) => {
        mostrar(`Hola, ${s.usuario.nombre.split(' ')[0]}`);
        navegar('/', { replace: true });
      })
      .catch((e: unknown) => {
        const causa = causaDelError(e);
        setErrorProveedor(causa ? mensajeGooglePersonal(causa, 'chofer', p) : SIN_RED);
        setConProveedor(null);
      });
  }, [mostrar, navegar]);

  function irCon(p: Proveedor) {
    setError(null);
    setErrorProveedor(null);
    setConProveedor({ p, paso: 'ida' });
    irAProveedor(p).catch(() => {
      setConProveedor(null);
      setErrorProveedor(`No se pudo abrir ${NOMBRE_PROVEEDOR[p]} en este celular. Entrá con tu correo y contraseña.`);
    });
  }

  async function entrar(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setErrorProveedor(null);
    const e = {
      email: !email.trim()
        ? 'Escribí tu correo'
        : !correoValido(email)
          ? 'Revisá el correo: por ejemplo nombre@empresa.com'
          : undefined,
      password: password ? undefined : 'Escribí tu contraseña',
    };
    setErrores(e);
    if (e.email || e.password) return;
    setOcupado(true);
    try {
      const s = await entrarConClave(email, password);
      mostrar(`Hola, ${s.usuario.nombre.split(' ')[0]}`);
      navegar('/', { replace: true });
    } catch (err) {
      if (err instanceof SinRed) setError(SIN_RED);
      else if (err instanceof ErrorApi) setError(err.message);
      else setError('No se pudo entrar. Probá de nuevo.');
    } finally {
      setOcupado(false);
    }
  }

  // Al volver del proveedor, mientras se confirma la sesión: la app en gris, como al abrirla.
  if (conProveedor?.paso === 'vuelta')
    return <PantallaNeutra texto={`Entrando con ${NOMBRE_PROVEEDOR[conProveedor.p]}…`} />;

  return (
    <MarcoAcceso>
      <form onSubmit={entrar} noValidate className="space-y-4">
        <div>
          <h2 className="text-xl font-medium">Entrá con tu correo</h2>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
            La primera vez, abrí el enlace que te mandó el administrador para crear tu contraseña.
          </p>
        </div>
        <Campo
          etiqueta="Correo"
          icono="mail"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="nombre@empresa.com"
          value={email}
          onChange={(e) => (setEmail(e.target.value), setErrores((x) => ({ ...x, email: undefined })))}
          error={errores.email}
        />
        <CampoClave
          etiqueta="Contraseña"
          autoComplete="current-password"
          value={password}
          onChange={(e) => (setPassword(e.target.value), setErrores((x) => ({ ...x, password: undefined })))}
          error={errores.password}
        />
        {error ? <AvisoError>{error}</AvisoError> : null}
        <Boton type="submit" bloque tamano="lg" icono="login" cargando={ocupado} disabled={!!conProveedor}>
          Entrar
        </Boton>
        <Link
          to="/acceso/olvide"
          state={{ email: email.trim() } satisfies EstadoAcceso}
          className="flex min-h-11 items-center justify-center text-[15px] font-medium text-primary-700 hover:underline dark:text-primary-300"
        >
          ¿Olvidaste tu contraseña?
        </Link>
      </form>
      {esperando ? (
        <ZonaCargando texto="Buscando con qué más se puede entrar…" className="mt-4 flex flex-col gap-3">
          <SeparadorO />
          {Array.from({ length: recordados }, (_, i) => (
            <Esqueleto key={i} className="h-14 w-full rounded-full" />
          ))}
        </ZonaCargando>
      ) : proveedores.length ? (
        <div className="mt-4 space-y-3">
          <SeparadorO />
          {errorProveedor ? <AvisoError>{errorProveedor}</AvisoError> : null}
          {proveedores.map((p) => (
            <BotonProveedor
              key={p}
              proveedor={p}
              tamano="lg"
              cargando={conProveedor?.p === p}
              disabled={ocupado || !!conProveedor}
              onClick={() => irCon(p)}
            />
          ))}
        </div>
      ) : errorProveedor ? (
        <div className="mt-4">
          <AvisoError>{errorProveedor}</AvisoError>
        </div>
      ) : null}
      <div className="mt-6 space-y-1 border-t border-stone-200 pt-4 dark:border-stone-800">
        {opciones?.celular ? (
          <EnlacePie a="/acceso/celular" icono="smartphone">
            Entrar con el celular
          </EnlacePie>
        ) : null}
        <EnlacePie a="/uso" icono="help">
          ¿Primera vez? Mirá cómo se usa
        </EnlacePie>
      </div>
    </MarcoAcceso>
  );
}
